import {
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TableBody,
  TableFooter,
  TablePagination,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { ExplanationAccordion, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { useEffect, useState } from 'react'
import { VehicleActivityLogParameters } from 'parameters'
import { VehicleActivityLogDto, VehicleDto } from 'dtos'
import { formatDateTime, useDebounce } from 'utils'
import { DEBOUNCE_DELAY } from 'localConstants'

interface ActivityLogDetailsCardProps {
  vehicle: VehicleDto | null
}

export default function ActivityLogDetailsCard({ vehicle }: ActivityLogDetailsCardProps) {
  const [isGettingVehicleActivityLogs, setIsGettingVehicleActivityLogs] = useState<boolean>(false)
  const [vehicleActivityLogs, setVehicleActivityLogs] = useState<VehicleActivityLogDto[]>([])

  const [totalCount, setTotalCount] = useState<number>(0)
  const [parameters, setParameters] = useState<VehicleActivityLogParameters>({
    page: 0,
    pageSize: 5,
    orderBy: 'date',
    order: 'desc'
  })

  const onChangeOrderBy = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle && vehicle.vehicleGuid) {
      getActivityLogs(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle, parameters])

  const getActivityLogs = useDebounce((vehicleGuid: string, parameters: VehicleActivityLogParameters) => {
    setIsGettingVehicleActivityLogs(true)
    controllers
      .getActivityLogs(vehicleGuid, parameters)
      .then(response => {
        setVehicleActivityLogs(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingVehicleActivityLogs(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={12}>
                <Typography fontWeight='bold' variant='h5'>
                  Activity Log
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {isGettingVehicleActivityLogs && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                  <Grid>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!isGettingVehicleActivityLogs && (
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='activityDateTime'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Time Stamp
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='person'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            User
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='message'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Message
                          </SortableTableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {vehicleActivityLogs.map(vehicleActivityLog => (
                          <TableRow>
                            <TableCell>
                              {vehicleActivityLog.activityDateTime ? formatDateTime(vehicleActivityLog.activityDateTime) : ''}
                            </TableCell>

                            <TableCell>
                              {vehicleActivityLog.person?.firstName} {vehicleActivityLog.person?.lastName}
                            </TableCell>

                            <TableCell>{vehicleActivityLog.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            count={totalCount}
                            onPageChange={(_, page) => {
                              setParameters({ ...parameters, page })
                            }}
                            onRowsPerPageChange={e => {
                              setParameters({ ...parameters, page: 0, pageSize: e.target.value as unknown as number })
                            }}
                            page={parameters.page}
                            rowsPerPage={parameters.pageSize}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            SelectProps={{ fullWidth: false }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <ExplanationAccordion variant='outlined'>
                      || Activity log shows notes automatically generated by VTS2 during user activity. || Records are added to
                      this screen when the vehicle record is created, when the Edit VIN dialog is saved, when any vehicle
                      details tabs (e.g., Exporter, Bond, Recall, Cluster, etc.) are saved, and if the vehicle import is
                      canceled. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
